import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator, useNavigate } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Container, ContainerButton } from '../components/MainStyledComponents';
import { BudgetItemsHistoricalTable } from '../../../../components/display/tables/BudgetItemsHistoricalTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';

import { usePersistentSelection } from '../../../../hooks/usePersistentSelection';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';
import { client } from '../../../../gql/client';
import {
  GET_ACTION_BUDGET_ITEMS_HISTORICAL,
  GET_ACTION_BUDGET_ITEM_BY_ID,
} from '../../../../gql/queries/budgets/actionBudgetItems';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { store } from '../../../../state/configureStore';
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { HistoricalBudgetData } from './types';

export default function SearchAndCreateHistoricalBudgetPage(): JSX.Element {
  const { t } = useTranslation(['budgets', 'pages']);
  const dispatch = useDispatch();
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  const navigate = useNavigate();
  const loaderData = useAsyncValue() as HistoricalBudgetData;

  // Persistent selection — all state managed by the hook
  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: loaderData.actionBudgetItems || [],
    totalCount: loaderData.totalCount || 0,
  });

  const onReFetchData = useCallback(async () => {
    const dataSelector = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(dataSelector.take, takeBase);

    const { data } = await client.query({
      query: GET_ACTION_BUDGET_ITEMS_HISTORICAL,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          orderBy: 'desc',
          budgetItemCode: dataSelector.budgetItemCode,
          categoryCode: dataSelector.categoryCode,
          fromDate: dataSelector.fromDate,
          toDate: dataSelector.toDate,
          indexExpedition: dataSelector.indexExpedition,
          indexType: dataSelector.indexType,
          concessionId: dataSelector.concessionId,
          pkInitKm: dataSelector.pkInitKm,
          pkInitMeter: dataSelector.pkInitMeter,
          pkEndKm: dataSelector.pkEndKm,
          pkEndMeter: dataSelector.pkEndMeter,
          externalCompanyId: dataSelector.externalCompanyId,
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      orderBy: 'desc',
      budgetItemCode: dataSelector?.budgetItemCode,
      categoryCode: dataSelector?.categoryCode,
      fromDate: dataSelector?.fromDate,
      toDate: dataSelector?.toDate,
      indexExpedition: dataSelector?.indexExpedition,
      indexType: dataSelector?.indexType,
      concessionId: dataSelector?.concessionId,
      pkInitKm: dataSelector?.pkInitKm,
      pkInitMeter: dataSelector?.pkInitMeter,
      pkEndKm: dataSelector?.pkEndKm,
      pkEndMeter: dataSelector?.pkEndMeter,
      externalCompanyId: dataSelector?.externalCompanyId,
    };

    dispatch(
      setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS, setCursorData)
    );
    revalidator.revalidate();
  }, []);

  const handleDownloadHistory = useCallback(() => {
    const dataSelector = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS)(store.getState());
    const { id: _id, take: _take, cursor: _cursor, ...filterFields } = dataSelector || {} as any;
    dispatch(
      openModal(modalIds.DOWNLOAD_HISTORIC, {
        type: 'ActionBudgetItems',
        filterPositionColumn: 2, // Columna del Código de Partida (posición 2 en tabla de presupuestos)
        selectedRows: selectAllMode ? [] : selectedRows,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: filterFields,
        totalCount: loaderData.totalCount || 0,
      })
    );
  }, [selectedRows, dispatch, selectAllMode, excludedIds, loaderData.totalCount]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const dataSelector = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS)(store.getState());
    const effectiveTake = firstPageTake(dataSelector.take, takeBase);
    await client.query({
      query: GET_ACTION_BUDGET_ITEMS_HISTORICAL,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          orderBy: 'desc',
          budgetItemCode: dataSelector.budgetItemCode,
          categoryCode: dataSelector.categoryCode,
          fromDate: dataSelector.fromDate,
          toDate: dataSelector.toDate,
          indexExpedition: dataSelector.indexExpedition,
          indexType: dataSelector.indexType,
          concessionId: dataSelector.concessionId,
          pkInitKm: dataSelector.pkInitKm,
          pkInitMeter: dataSelector.pkInitMeter,
          pkEndKm: dataSelector.pkEndKm,
          pkEndMeter: dataSelector.pkEndMeter,
          externalCompanyId: dataSelector.externalCompanyId,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);

  const handleViewSummary = useCallback(() => {
    if (selectedRows[0]) {
      // Navigate directly to summary using the budget item ID
      navigate(`summary/${selectedRows[0]}`);
    }
  }, [selectedRows, navigate]);

  return (
    <Container>
      <BudgetItemsHistoricalTable
        idCss="historical-ActionBudgetItems"
        titleText={t('searchAndCreateHistorical.title')}
        data={{
          actionBudgetItems: loaderData.actionBudgetItems || [],
          filtersDataOptions: loaderData.filtersDataOptions || {
            budgetCategories: [],
            concessions: [],
          },
          totalCount: loaderData.totalCount || 0,
          pageInfo: loaderData.pageInfo || {
            hasNextPage: false,
            hasPreviousPage: false,
          },
          loading: loaderData.loading || false,
        }}
        onReFetchData={onReFetchData}
        rowState={selectedRows}
        setRowState={() => {}}
        tableId={tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS}
        onReFetchPagination={onRefetchPagination}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectedCount={selectedCount}
      />

      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('pages:budgets.viewSummary')}
          disabled={selectedCount !== 1}
          onClick={handleViewSummary}
        />
        <MainButton
          text={t('pages:budgets.downloadHistory')}
          disabled={selectedCount === 0}
          onClick={handleDownloadHistory}
        />
      </ContainerButton>
    </Container>
  );
}
